import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "parent" | "student" | "school_admin" | "merchant" | "superadmin" | "finance_officer";

export type AccountStatus = "pending" | "approved" | "rejected" | "suspended";

type Profile = {
  id: string;
  full_name: string | null;
  role: AppRole | null;
  school_id: string | null;
  account_status: AccountStatus | null;
};

type AuthContextValue = {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isSuperadmin: boolean;
  tenantId: string | null;
  tenantSlug: string | null;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  profile: null,
  session: null,
  loading: true,
  signOut: async () => {},
  isSuperadmin: false,
  tenantId: null,
  tenantSlug: null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      if (!data.session) {
        currentUserIdRef.current = null;
        setLoading(false);
      } else {
        currentUserIdRef.current = data.session.user?.id ?? null;
      }
    }).catch(() => {
      if (!mounted) return;
      setSession(null);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      if (!newSession) {
        currentUserIdRef.current = null;
        setProfile(null);
        setLoading(false);
      } else {
        const newUserId = newSession.user?.id ?? null;
        const userIdChanged = currentUserIdRef.current !== newUserId;
        currentUserIdRef.current = newUserId;
        if (event === "SIGNED_IN" || event === "USER_UPDATED" || userIdChanged) {
          setProfile(prev => {
            if (!userIdChanged && prev && event !== "USER_UPDATED") {
              return prev;
            }
            setLoading(true);
            return prev;
          });
        }
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.user) {
      setProfile(null);
      return;
    }
    let active = true;

    Promise.all([
      supabase.from("profiles").select("id, full_name, school_id").eq("id", session.user.id).maybeSingle(),
      supabase.from("user_roles").select("role, school_id, account_status").eq("user_id", session.user.id).limit(1).maybeSingle(),
    ]).then(([profileRes, roleRes]) => {
      if (!active) return;
      if (profileRes.data) {
        setProfile({
          id: profileRes.data.id,
          full_name: profileRes.data.full_name,
          role: (roleRes.data?.role as AppRole) ?? null,
          school_id: roleRes.data?.school_id ?? profileRes.data.school_id ?? null,
          account_status: (roleRes.data?.account_status as AccountStatus) ?? null,
        });
      } else {
        setProfile({
          id: session.user.id,
          full_name: session.user.user_metadata?.full_name ?? null,
          role: (roleRes.data?.role as AppRole) ?? null,
          school_id: roleRes.data?.school_id ?? null,
          account_status: (roleRes.data?.account_status as AccountStatus) ?? null,
        });
      }
      if (active) setLoading(false);
    }).catch(() => {
      if (!active) return;
      setProfile({
        id: session.user.id,
        full_name: session.user.user_metadata?.full_name ?? null,
        role: null,
        school_id: null,
        account_status: null,
      });
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [session?.user?.id]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setSession(null);
    setLoading(false);
    if (typeof window !== "undefined") {
      window.location.href = "/auth";
    }
  };

  const isSuperadmin = profile?.role === "superadmin";
  const tenantId = profile?.school_id ?? null;
  const tenantSlug = profile?.school_id ?? null;

  return (
    <AuthContext.Provider
      value={{ user: session?.user ?? null, profile, session, loading, signOut, isSuperadmin, tenantId, tenantSlug }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
